import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// Campo de fútbol: 105m x 68m (proporción real)
const FIELD_WIDTH = 1680; // 105m
const FIELD_HEIGHT = 1088; // 68m
const PLAYER_RADIUS = 22;
const BALL_RADIUS = 15;

export const FootballBoard = ({ players, ball, onDragPlayer, onDragBall, drawings, onDrawingComplete }) => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [drawingPoints, setDrawingPoints] = useState([]);
  const [currentTool, setCurrentTool] = useState(null);
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [isDrawing, setIsDrawing] = useState(false);
  const [freeDrawPath, setFreeDrawPath] = useState([]);

  // Calculate scale based on container size
  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const containerHeight = containerRef.current.offsetHeight;
        const scaleX = (containerWidth - 16) / FIELD_WIDTH;
        const scaleY = (containerHeight - 16) / FIELD_HEIGHT;
        setScale(Math.min(scaleX, scaleY, 1));
      }
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  const drawField = useCallback((ctx) => {
    // Grass background
    const grassGradient = ctx.createLinearGradient(0, 0, 0, FIELD_HEIGHT);
    grassGradient.addColorStop(0, '#1a7a3e');
    grassGradient.addColorStop(0.5, '#2d9c52');
    grassGradient.addColorStop(1, '#1a7a3e');
    ctx.fillStyle = grassGradient;
    ctx.fillRect(0, 0, FIELD_WIDTH, FIELD_HEIGHT);

    // Grass stripes
    ctx.fillStyle = 'rgba(0, 0, 0, 0.03)';
    const stripeWidth = FIELD_WIDTH / 10;
    for (let i = 0; i < 10; i += 2) {
      ctx.fillRect(i * stripeWidth, 0, stripeWidth, FIELD_HEIGHT);
    }

    // White lines
    ctx.strokeStyle = '#ffffff';
    ctx.fillStyle = '#ffffff';
    ctx.lineWidth = 4;

    // Outer boundary
    ctx.strokeRect(0, 0, FIELD_WIDTH, FIELD_HEIGHT);

    // Center line
    ctx.beginPath();
    ctx.moveTo(FIELD_WIDTH / 2, 0);
    ctx.lineTo(FIELD_WIDTH / 2, FIELD_HEIGHT);
    ctx.stroke();

    // Center circle
    const centerX = FIELD_WIDTH / 2;
    const centerY = FIELD_HEIGHT / 2;
    const centerCircleRadius = (9.15 / 68) * FIELD_HEIGHT; // 9.15m radius
    ctx.beginPath();
    ctx.arc(centerX, centerY, centerCircleRadius, 0, Math.PI * 2);
    ctx.stroke();

    // Center spot
    ctx.beginPath();
    ctx.arc(centerX, centerY, 6, 0, Math.PI * 2);
    ctx.fill();

    // Penalty areas (16.5m from goal line)
    const penaltyAreaWidth = (40.32 / 105) * FIELD_WIDTH; // 40.32m
    const penaltyAreaDepth = (16.5 / 105) * FIELD_WIDTH; // 16.5m

    // Left penalty area
    ctx.strokeRect(
      0,
      (FIELD_HEIGHT - penaltyAreaWidth) / 2,
      penaltyAreaDepth,
      penaltyAreaWidth
    );

    // Right penalty area
    ctx.strokeRect(
      FIELD_WIDTH - penaltyAreaDepth,
      (FIELD_HEIGHT - penaltyAreaWidth) / 2,
      penaltyAreaDepth,
      penaltyAreaWidth
    );

    // Goal areas (5.5m from goal line)
    const goalAreaWidth = (18.32 / 105) * FIELD_WIDTH; // 18.32m
    const goalAreaDepth = (5.5 / 105) * FIELD_WIDTH; // 5.5m

    // Left goal area
    ctx.strokeRect(
      0,
      (FIELD_HEIGHT - goalAreaWidth) / 2,
      goalAreaDepth,
      goalAreaWidth
    );

    // Right goal area
    ctx.strokeRect(
      FIELD_WIDTH - goalAreaDepth,
      (FIELD_HEIGHT - goalAreaWidth) / 2,
      goalAreaDepth,
      goalAreaWidth
    );

    // Penalty spots
    const penaltySpotDistance = (11 / 105) * FIELD_WIDTH; // 11m from goal
    ctx.beginPath();
    ctx.arc(penaltySpotDistance, centerY, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(FIELD_WIDTH - penaltySpotDistance, centerY, 6, 0, Math.PI * 2);
    ctx.fill();

    // Penalty arcs
    const penaltyArcRadius = (9.15 / 68) * FIELD_HEIGHT;
    ctx.beginPath();
    ctx.arc(penaltySpotDistance, centerY, penaltyArcRadius, -Math.PI / 3, Math.PI / 3);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(FIELD_WIDTH - penaltySpotDistance, centerY, penaltyArcRadius, Math.PI * 2 / 3, Math.PI * 4 / 3);
    ctx.stroke();

    // Corner arcs
    const cornerRadius = (1 / 105) * FIELD_WIDTH;
    // Top-left
    ctx.beginPath();
    ctx.arc(0, 0, cornerRadius, 0, Math.PI / 2);
    ctx.stroke();
    // Top-right
    ctx.beginPath();
    ctx.arc(FIELD_WIDTH, 0, cornerRadius, Math.PI / 2, Math.PI);
    ctx.stroke();
    // Bottom-left
    ctx.beginPath();
    ctx.arc(0, FIELD_HEIGHT, cornerRadius, -Math.PI / 2, 0);
    ctx.stroke();
    // Bottom-right
    ctx.beginPath();
    ctx.arc(FIELD_WIDTH, FIELD_HEIGHT, cornerRadius, Math.PI, Math.PI * 3 / 2);
    ctx.stroke();
  }, []);

  const drawPlayer = useCallback((ctx, x, y, color, number, isGoalkeeper, isBlaugrana) => {
    // Shadow
    ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
    ctx.shadowBlur = 10;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 3;

    // Player circle
    if (isBlaugrana) {
      // Rayas azul y grana (Barcelona)
      const stripeWidth = PLAYER_RADIUS / 2.5;
      for (let i = 0; i < 5; i++) {
        ctx.fillStyle = i % 2 === 0 ? '#004C99' : '#A50044';
        ctx.beginPath();
        ctx.arc(x, y, PLAYER_RADIUS, 0, Math.PI * 2);
        ctx.fill();
        ctx.save();
        ctx.clip();
        ctx.fillRect(x - PLAYER_RADIUS + i * stripeWidth, y - PLAYER_RADIUS, stripeWidth, PLAYER_RADIUS * 2);
        ctx.restore();
      }
      // Fill with stripes pattern
      ctx.fillStyle = '#004C99';
      ctx.beginPath();
      ctx.arc(x, y, PLAYER_RADIUS, 0, Math.PI * 2);
      ctx.fill();
      
      // Draw vertical stripes
      ctx.fillStyle = '#A50044';
      const numStripes = 4;
      const totalStripeWidth = PLAYER_RADIUS * 2;
      const singleStripeWidth = totalStripeWidth / (numStripes * 2);
      
      ctx.save();
      ctx.beginPath();
      ctx.arc(x, y, PLAYER_RADIUS, 0, Math.PI * 2);
      ctx.clip();
      
      for (let i = 0; i < numStripes; i++) {
        const stripeX = x - PLAYER_RADIUS + (i * 2 + 1) * singleStripeWidth;
        ctx.fillRect(stripeX, y - PLAYER_RADIUS, singleStripeWidth, PLAYER_RADIUS * 2);
      }
      ctx.restore();
    } else {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, PLAYER_RADIUS, 0, Math.PI * 2);
      ctx.fill();
    }

    // Border (thicker for goalkeeper)
    ctx.shadowColor = 'transparent';
    ctx.strokeStyle = isGoalkeeper ? '#FFD700' : '#ffffff';
    ctx.lineWidth = isGoalkeeper ? 4 : 2;
    ctx.beginPath();
    ctx.arc(x, y, PLAYER_RADIUS, 0, Math.PI * 2);
    ctx.stroke();

    // Number - color depends on jersey color
    if (color === '#ffffff') {
      // Jugadores blancos - número negro
      ctx.fillStyle = '#000000';
    } else {
      // Otros equipos - número blanco
      ctx.fillStyle = '#ffffff';
    }
    ctx.font = 'bold 16px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(number.toString(), x, y);
  }, []);

  const drawBall = useCallback((ctx, x, y) => {
    // Shadow
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 8;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 3;

    // Ball - white with black pentagons pattern
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(x, y, BALL_RADIUS, 0, Math.PI * 2);
    ctx.fill();

    // Black pentagons (simplified pattern)
    ctx.shadowColor = 'transparent';
    ctx.fillStyle = '#000000';
    ctx.beginPath();
    ctx.arc(x - 5, y - 3, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x + 5, y - 3, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x, y + 5, 4, 0, Math.PI * 2);
    ctx.fill();

    // Border
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x, y, BALL_RADIUS, 0, Math.PI * 2);
    ctx.stroke();
  }, []);

  const drawLine = useCallback((ctx, from, to) => {
    ctx.strokeStyle = 'rgba(255, 0, 0, 0.8)';
    ctx.lineWidth = 6;
    ctx.lineCap = 'round';
    ctx.setLineDash([]);
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = 8;
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
    
    // Arrow head
    const angle = Math.atan2(to.y - from.y, to.x - from.x);
    const arrowLength = 25;
    ctx.beginPath();
    ctx.moveTo(to.x, to.y);
    ctx.lineTo(
      to.x - arrowLength * Math.cos(angle - Math.PI / 6),
      to.y - arrowLength * Math.sin(angle - Math.PI / 6)
    );
    ctx.moveTo(to.x, to.y);
    ctx.lineTo(
      to.x - arrowLength * Math.cos(angle + Math.PI / 6),
      to.y - arrowLength * Math.sin(angle + Math.PI / 6)
    );
    ctx.stroke();
    ctx.shadowColor = 'transparent';
  }, []);

  const drawCircle = useCallback((ctx, center, radius) => {
    ctx.strokeStyle = 'rgba(0, 0, 255, 0.8)';
    ctx.fillStyle = 'rgba(0, 0, 255, 0.2)';
    ctx.lineWidth = 6;
    ctx.setLineDash([10, 10]);
    ctx.beginPath();
    ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.setLineDash([]);
  }, []);

  const drawFreePath = useCallback((ctx, points) => {
    if (points.length < 2) return;
    
    ctx.strokeStyle = 'rgba(255, 165, 0, 0.9)';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.setLineDash([]);
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = 4;
    
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    ctx.stroke();
    ctx.shadowColor = 'transparent';
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, FIELD_WIDTH, FIELD_HEIGHT);

    drawField(ctx);

    // Draw existing drawings
    drawings.forEach((drawing) => {
      if (drawing.type === 'line') {
        drawLine(ctx, drawing.from, drawing.to);
      } else if (drawing.type === 'circle') {
        drawCircle(ctx, drawing.center, drawing.radius);
      } else if (drawing.type === 'freepath') {
        drawFreePath(ctx, drawing.points);
      }
    });

    // Draw temporary drawing
    if (drawingPoints.length > 0) {
      if (currentTool === 'line' && drawingPoints.length === 2) {
        drawLine(ctx, drawingPoints[0], drawingPoints[1]);
      } else if (currentTool === 'circle' && drawingPoints.length === 2) {
        const radius = Math.hypot(
          drawingPoints[1].x - drawingPoints[0].x,
          drawingPoints[1].y - drawingPoints[0].y
        );
        drawCircle(ctx, drawingPoints[0], radius);
      }
    }

    // Draw current free drawing path
    if (currentTool === 'freedraw' && freeDrawPath.length > 0) {
      drawFreePath(ctx, freeDrawPath);
    }

    // Draw players
    players.forEach((player) => {
      drawPlayer(ctx, player.x, player.y, player.color, player.number, player.isGoalkeeper, player.isBlaugrana);
    });

    // Draw ball
    drawBall(ctx, ball.x, ball.y);
  }, [players, ball, drawings, drawingPoints, currentTool, freeDrawPath, drawField, drawPlayer, drawBall, drawLine, drawCircle, drawFreePath]);

  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / scale,
      y: (e.clientY - rect.top) / scale,
    };
  };

  const getTouchPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    return {
      x: (touch.clientX - rect.left) / scale,
      y: (touch.clientY - rect.top) / scale,
    };
  };

  const handleStart = (pos) => {
    if (currentTool === 'freedraw') {
      setIsDrawing(true);
      setFreeDrawPath([pos]);
      return;
    }

    if (currentTool) {
      setDrawingPoints([...drawingPoints, pos]);
      
      if ((currentTool === 'line' || currentTool === 'circle') && drawingPoints.length === 1) {
        const newDrawing = currentTool === 'line'
          ? { type: 'line', from: drawingPoints[0], to: pos }
          : {
              type: 'circle',
              center: drawingPoints[0],
              radius: Math.hypot(pos.x - drawingPoints[0].x, pos.y - drawingPoints[0].y),
            };
        onDrawingComplete(newDrawing);
        setDrawingPoints([]);
      }
      return;
    }

    // Check if clicking on player
    const clickedPlayer = players.findIndex((player) => {
      const distance = Math.hypot(player.x - pos.x, player.y - pos.y);
      return distance <= PLAYER_RADIUS;
    });

    if (clickedPlayer !== -1) {
      setDraggedItem({ type: 'player', index: clickedPlayer });
      return;
    }

    // Check if clicking on ball
    const distanceToBall = Math.hypot(ball.x - pos.x, ball.y - pos.y);
    if (distanceToBall <= BALL_RADIUS) {
      setDraggedItem({ type: 'ball' });
    }
  };

  const handleMove = (pos) => {
    // Free drawing mode
    if (currentTool === 'freedraw' && isDrawing) {
      setFreeDrawPath(prev => [...prev, pos]);
      return;
    }

    if (!draggedItem) return;

    // Limitar posición dentro de los límites del campo
    let x = pos.x;
    let y = pos.y;

    if (draggedItem.type === 'player') {
      x = Math.max(PLAYER_RADIUS, Math.min(FIELD_WIDTH - PLAYER_RADIUS, pos.x));
      y = Math.max(PLAYER_RADIUS, Math.min(FIELD_HEIGHT - PLAYER_RADIUS, pos.y));
      onDragPlayer(draggedItem.index, x, y);
    } else if (draggedItem.type === 'ball') {
      x = Math.max(BALL_RADIUS, Math.min(FIELD_WIDTH - BALL_RADIUS, pos.x));
      y = Math.max(BALL_RADIUS, Math.min(FIELD_HEIGHT - BALL_RADIUS, pos.y));
      onDragBall(x, y);
    }
  };

  const handleEnd = () => {
    if (currentTool === 'freedraw' && isDrawing && freeDrawPath.length > 1) {
      onDrawingComplete({ type: 'freepath', points: freeDrawPath });
      setFreeDrawPath([]);
      setIsDrawing(false);
      return;
    }
    
    setDraggedItem(null);
    setIsDrawing(false);
  };

  const handleMouseDown = (e) => {
    const pos = getMousePos(e);
    handleStart(pos);
  };

  const handleMouseMove = (e) => {
    const pos = getMousePos(e);
    handleMove(pos);
  };

  const handleMouseUp = () => {
    handleEnd();
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    const pos = getTouchPos(e);
    handleStart(pos);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    const pos = getTouchPos(e);
    handleMove(pos);
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    handleEnd();
  };

  useEffect(() => {
    const handleToolChange = (e) => {
      setCurrentTool(e.detail.tool);
      setDrawingPoints([]);
    };

    window.addEventListener('toolchange', handleToolChange);
    return () => window.removeEventListener('toolchange', handleToolChange);
  }, []);

  return (
    <div ref={containerRef} className="flex items-center justify-center w-full h-full p-2">
      <canvas
        ref={canvasRef}
        width={FIELD_WIDTH}
        height={FIELD_HEIGHT}
        className={`shadow-court rounded-sm touch-none ${currentTool === 'freedraw' ? 'cursor-crosshair' : 'cursor-move'}`}
        style={{
          width: `${FIELD_WIDTH * scale}px`,
          height: `${FIELD_HEIGHT * scale}px`,
          maxWidth: '100%',
          maxHeight: '100%',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      />
    </div>
  );
};